import { isSelectOption } from "typeGuards/isSelectOption";
import { NumberSchema, number, string } from "yup";
import { splitMultilineCommaSeparatedList } from "utils/splitMultilineCommaSeparatedList";
import { ERROR_MESSAGES, REGEX } from "../../constants";
import {
  DATABASE_ERROR_MESSAGES,
  DATABASE_REGEX,
  RESERVED_DATABASE_NAMES,
  RESERVED_DATABASE_USERS
} from "./constants";
import { supportsNamedDatabases } from "./selectors";
import { DBServiceParams } from "./types";

// A custom parameter value the operator would drop: a space, a quote or a control character.
export const isParameterValue = (value?: string): boolean =>
  ![...(value ?? "")].some((char) => {
    const code = char.charCodeAt(0);
    return (
      char === " " || char === '"' || char === "'" || code < 32 || code === 127
    );
  });

const notReserved = (reserved: string[]) => (value?: string) =>
  !value || !reserved.includes(value.toLowerCase());

// The instance name is also its first database's name, except on engines with none.
export const instanceNameSchema = string()
  .required()
  .matches(
    REGEX.NAME_STARTS_WITH_LETTER,
    ERROR_MESSAGES.NAME_STARTS_WITH_LETTER
  )
  .matches(DATABASE_REGEX.NAME, DATABASE_ERROR_MESSAGES.NAME)
  .test(
    "reservedDatabaseName",
    DATABASE_ERROR_MESSAGES.RESERVED_NAME,
    (value, { parent }) => {
      const { serviceName } = parent as { serviceName?: unknown };
      return (
        !(
          isSelectOption(serviceName) &&
          supportsNamedDatabases(serviceName.value)
        ) || notReserved(RESERVED_DATABASE_NAMES)(value)
      );
    }
  );

export const instanceUserSchema = string()
  .matches(REGEX.NAME_STARTS_WITH_LETTER, {
    message: ERROR_MESSAGES.NAME_STARTS_WITH_LETTER,
    excludeEmptyString: true
  })
  .matches(DATABASE_REGEX.NAME, {
    message: DATABASE_ERROR_MESSAGES.NAME,
    excludeEmptyString: true
  })
  .test(
    "reservedDatabaseUser",
    DATABASE_ERROR_MESSAGES.RESERVED_USER,
    notReserved(RESERVED_DATABASE_USERS)
  );

export const childDatabaseNameSchema = string()
  .required()
  .matches(DATABASE_REGEX.CHILD_NAME, DATABASE_ERROR_MESSAGES.CHILD_NAME)
  .test(
    "reservedDatabaseName",
    DATABASE_ERROR_MESSAGES.RESERVED_NAME,
    notReserved(RESERVED_DATABASE_NAMES)
  );

export const userNameSchema = string()
  .required()
  .matches(DATABASE_REGEX.USER_NAME, DATABASE_ERROR_MESSAGES.USER_NAME)
  .test(
    "reservedDatabaseUser",
    DATABASE_ERROR_MESSAGES.RESERVED_USER,
    notReserved(RESERVED_DATABASE_USERS)
  );

export const ipACLSchema = string().test(
  "IPACLValidFormat",
  DATABASE_ERROR_MESSAGES.IP_ACL,
  (value) =>
    !value?.trim() ||
    splitMultilineCommaSeparatedList(value).every((record) =>
      DATABASE_REGEX.IP_CIDR.test(record)
    )
);

// The API checks the disk against the engine's own range, bounds included.
export const diskSizeSchema = (catalogue?: DBServiceParams[] | null) =>
  number()
    .integer()
    .when("serviceName", ([serviceName], schema: NumberSchema) => {
      const params = catalogue?.find(
        (p) =>
          isSelectOption(serviceName) && p.service_name === serviceName.value
      );
      return params
        ? schema
            .min(
              Number(params.disk_min),
              `Disk size must be greater than or equals to ${params.disk_min} MiB.`
            )
            .max(
              Number(params.disk_max),
              `Disk size must be less than or equals to ${params.disk_max} MiB.`
            )
        : schema;
    });
